// src/oura/delivery/timelineDownload.ts
//
// "Download timeline": the client-facing timeline, month by month, printed
// from Delivery's real states in the client's words (the client line, never
// staff steps). A self-contained HTML file; print to PDF from the browser.

import type { DeliveryPayload } from "@service/aeo/delivery";
import { dayLabel } from "@service/aeo/delivery";

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const WORD_COLOUR: Record<string, string> = { Done: "#1E7B38", "In progress": "#0B49FE", "Needs you": "#B45309", "Not started": "#6B7280" };

function fileDate(d: Date): string {
    const m = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
    return `${d.getDate()} ${m} ${d.getFullYear()}`;
}

export function downloadDeliveryTimeline(p: DeliveryPayload): string {
    const last = p.termMonths || p.deliverables.reduce((m, d) => Math.max(m, d.month), 0);
    const months = Array.from({ length: last }, (_, i) => i + 1);
    const today = new Date();
    const body = months
        .map((m) => {
            const rows = p.deliverables.filter((d) => d.month === m);
            if (rows.length === 0) return "";
            const win = rows[0].window;
            const head = m === 1 ? "Month 1 · Foundation" : `Month ${m}`;
            return `
    <section>
      <h2>${esc(head)}${win ? ` <span>${esc(dayLabel(win.start))} to ${esc(dayLabel(win.end))}</span>` : ""}</h2>
      <ul>
        ${rows
            .map(
                (d) => {
                    // The client line whole, its state word in colour: "Done 12 Oct. Our scan found 0 broken pages."
                    const rest = d.clientLine.startsWith(d.clientWord) ? d.clientLine.slice(d.clientWord.length) : ` ${d.clientLine}`;
                    return `<li><b class="code">${esc(d.code)}</b><div><p class="label">${esc(d.label)}</p><p class="line"><span style="color:${WORD_COLOUR[d.clientWord] ?? "#374151"}">${esc(d.clientWord)}</span>${esc(rest)}</p></div></li>`;
                },
            )
            .join("")}
      </ul>
    </section>`;
        })
        .join("");
    const title = `${p.businessName} · Timeline`;
    const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; margin: 0; }
  body { font-family: Inter, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111827; background: #fff; padding: 48px 24px; max-width: 860px; margin: 0 auto; line-height: 1.5; font-size: 14px; }
  .brand { color: #0B49FE; font-weight: 600; font-size: 18px; letter-spacing: -0.01em; }
  h1 { font-size: 28px; font-weight: 600; margin: 18px 0 4px; letter-spacing: -0.02em; }
  .sub { color: #4B5563; margin-bottom: 28px; }
  section { border-top: 1px solid #E5E7EB; padding: 18px 0 8px; break-inside: avoid; }
  h2 { font-size: 17px; font-weight: 600; margin-bottom: 10px; }
  h2 span { font-weight: 400; color: #6B7280; font-size: 13px; margin-left: 8px; }
  ul { list-style: none; padding: 0; }
  li { display: flex; gap: 12px; padding: 8px 0; border-top: 1px solid #F3F4F6; }
  li:first-child { border-top: 0; }
  .code { display: inline-flex; align-items: center; justify-content: center; min-width: 34px; height: 22px; border-radius: 6px; background: #E8F1FF; color: #0A5FD1; font-size: 12px; font-weight: 600; }
  .label { font-weight: 500; }
  .line { color: #4B5563; font-size: 13px; }
  footer { margin-top: 28px; color: #6B7280; font-size: 12px; }
</style>
</head>
<body>
  <div class="brand">ourteam</div>
  <h1>${esc(p.businessName)}</h1>
  <p class="sub">Your plan, month by month, as it stands on ${esc(fileDate(today))}. ${esc(p.monthLabel)}: ${p.doneCount.done} of ${p.doneCount.total} done.</p>
  ${body}
  <footer>Prepared by ourteam. Each line is the state of that deliverable today.</footer>
</body>
</html>`;
    const name = `ourteam_${p.businessName}_Timeline_v1.0_${fileDate(today)}.html`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    return name;
}
